import { PickerPosition, formatDate, SelectType } from '@ui/src';
import { useAppSelector } from '@store';
import GeneralUserInfoRow from './general-user-info-row';
import { IdType, OrganizationType } from '../../../hoc/customers/types';

import { GeneralUserInfoBlockTypes } from '../types';

import cn from './overview-content.module.css';

const idTypesArray = [
  'ID_CARD',
  'PASSPORT',
  'DRIVERS',
  'RESIDENCE_PERMIT',
  'UTILITY_BILL',
  'SELFIE',
  'VIDEO_SELFIE',
  'PROFILE_IMAGE',
  'ID_DOC_PHOTO',
  'AGREEMENT',
  'CONTRACT',
  'DRIVERS_TRANSLATION',
  'INVESTOR_DOC',
  'VEHICLE_REGISTRATION_CERTIFICATE',
  'INCOME_SOURCE',
  'PAYMENT_METHOD',
  'BANK_CARD',
  'COVID_VACCINATION_FORM',
  'OTHER',
];

const getIdTypeName = (name: IdType) => {
  let idTypeName;

  switch (name) {
    case IdType.AGREEMENT:
      idTypeName = 'Agreement';
      break;
    case IdType.BANK_CARD:
      idTypeName = 'Bank Card';
      break;
    case IdType.CONTRACT:
      idTypeName = 'Contract';
      break;
    case IdType.COVID_VACCINATION_FORM:
      idTypeName = 'Covid Vaccination Form';
      break;
    case IdType.DRIVERS:
      idTypeName = 'Drivers';
      break;
    case IdType.DRIVERS_TRANSLATION:
      idTypeName = 'Drivers Translation';
      break;
    case IdType.ID_CARD:
      idTypeName = 'ID Card';
      break;
    case IdType.ID_DOC_PHOTO:
      idTypeName = 'ID Doc Photo';
      break;
    case IdType.INCOME_SOURCE:
      idTypeName = 'Income Source';
      break;
    case IdType.INVESTOR_DOC:
      idTypeName = 'Investor Doc';
      break;
    case IdType.OTHER:
      idTypeName = 'Other';
      break;
    case IdType.PASSPORT:
      idTypeName = 'Passport';
      break;
    case IdType.PAYMENT_METHOD:
      idTypeName = 'Payment Method';
      break;
    case IdType.PROFILE_IMAGE:
      idTypeName = 'Profile Image';
      break;
    case IdType.RESIDENCE_PERMIT:
      idTypeName = 'Residence Permit';
      break;
    case IdType.SELFIE:
      idTypeName = 'Selfie';
      break;
    case IdType.UTILITY_BILL:
      idTypeName = 'Utility Bill';
      break;
    case IdType.VEHICLE_REGISTRATION_CERTIFICATE:
      idTypeName = 'Vehicle Registration Certificate';
      break;
    case IdType.VIDEO_SELFIE:
      idTypeName = 'Video Selfie';
      break;
    default:
      idTypeName = name;
      break;
  }

  return idTypeName;
};

const getCurrentAge = (birthDate: string) => {
  const today = new Date();
  const birthDateObj = new Date(birthDate);

  let age = today.getFullYear() - birthDateObj.getFullYear();
  const monthDiff = today.getMonth() - birthDateObj.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDateObj.getDate())
  ) {
    age -= 1;
  }

  return age;
};

const GeneralUserInfoBlock = ({
  generalUserInfo,
  control,
  baseUserInformation,
  globalError,
  errors,
  edit,
}: GeneralUserInfoBlockTypes) => {
  const { sourcesOfFunds, countries, clientsActivities } = useAppSelector(
    (state) => state.customersDictionaries,
  );

  return (
    <div
      className={cn.generalUserInfoBlock}
      style={{ zIndex: 10, position: 'relative' }}
    >
      <div
        className={`${cn.generalUserInfoForm} ${cn.generalUserInfoFormFlex}`}
      >
        <div
          className={`${cn.generalUserInfoBlockItem} ${cn.generalUserInfoBlockItemLeft}`}
        >
          {!edit && (
            <GeneralUserInfoRow
              title="Full Name"
              control={control}
              type="text"
              disabled={!edit}
              defaultValue={baseUserInformation?.fullName}
              withCopy
            />
          )}
          {edit && (
            <>
              <GeneralUserInfoRow
                title="First Name"
                control={control}
                type="text"
                disabled={!edit}
                name="firstName"
                error={errors?.firstName?.message}
              />
              <GeneralUserInfoRow
                title="Last Name"
                control={control}
                type="text"
                name="lastName"
                disabled={!edit}
                error={errors?.lastName?.message}
              />
            </>
          )}
          {!edit && (
            <GeneralUserInfoRow
              title="Date of birth"
              control={control}
              type="text"
              name="dateOfBirth"
              defaultValue={
                baseUserInformation?.dateOfBirth
                  ? formatDate(
                      new Date(baseUserInformation?.dateOfBirth).toISOString(),
                    )
                  : '-'
              }
              disabled={!edit}
              withCopy={!edit}
              birthdayPicker
            />
          )}
          {edit && (
            <GeneralUserInfoRow
              title="Date of birth"
              control={control}
              type="date"
              name="dateOfBirth"
              defaultValue={
                baseUserInformation?.dateOfBirth
                  ? formatDate(
                      new Date(baseUserInformation?.dateOfBirth).toISOString(),
                    )
                  : '-'
              }
              disabled={!edit}
              birthdayPicker
            />
          )}
          <GeneralUserInfoRow
            title="Nationality"
            control={control}
            type="autocomplete"
            disabled={!edit}
            list={countries}
            name="nationalityId"
            defaultValue={
              countries?.find(
                (item) => item.value === generalUserInfo?.nationalityId,
              )?.name || '-'
            }
            error={errors?.nationalityId?.message}
            withCopy={!edit}
          />
          {!edit && (
            <GeneralUserInfoRow
              title="Age"
              control={control}
              type="default"
              defaultValue={`
              ${
                baseUserInformation?.dateOfBirth
                  ? getCurrentAge(baseUserInformation?.dateOfBirth)
                  : '-'
              }
              y.o
            `}
              disabled
              withCopy
            />
          )}
          {generalUserInfo?.type !== OrganizationType.COMPANY_OFFICIAL && (
            <>
              <GeneralUserInfoRow
                title="Client Activity"
                control={control}
                type="select"
                disabled={!edit}
                name="clientActivityId"
                defaultValue={
                  clientsActivities?.find(
                    (item) =>
                      item.value === baseUserInformation?.clientActivityId,
                  )?.name || '-'
                }
                list={clientsActivities || []}
                error={errors?.clientActivityId?.message}
                withCopy={!edit}
              />
              {(!generalUserInfo?.clientScheme ||
                (generalUserInfo?.clientScheme &&
                  generalUserInfo?.clientScheme?.id !==
                    'cf8b9900-6624-4854-b932-981c2af391b6')) && (
                <GeneralUserInfoRow
                  title="Source of funds"
                  control={control}
                  type="select"
                  disabled={!edit}
                  name="sourceOfFundsIds"
                  selectType={SelectType.MULTIPLE}
                  defaultValue={sourcesOfFunds
                    ?.filter((item) => {
                      let result;

                      if (
                        baseUserInformation?.sourceOfFunds?.find(
                          (sof) => sof === item.value,
                        )
                      ) {
                        result = item;
                      }

                      return result;
                    })
                    .map((item) => item.name)
                    .join(', ')}
                  list={sourcesOfFunds}
                  error={errors?.sourceOfFundsIds?.message}
                  withCopy={!edit}
                />
              )}
            </>
          )}
          <GeneralUserInfoRow
            title="TAX Number"
            control={control}
            type="text"
            disabled={!edit}
            name="taxNumber"
            defaultValue={generalUserInfo?.taxNumber || '-'}
            withCopy={!edit}
          />
        </div>
        <div className={cn.generalUserInfoBlockItem}>
          <GeneralUserInfoRow
            title="ID Number"
            control={control}
            type="text"
            disabled={!edit}
            name="idNumber"
            error={errors?.idNumber?.message}
            defaultValue={generalUserInfo?.idNumber || '-'}
            withCopy={!edit}
          />
          <GeneralUserInfoRow
            title="ID Type"
            control={control}
            type="select"
            name="idType"
            disabled={!edit}
            list={idTypesArray.map((item) => ({
              name: getIdTypeName(item as IdType),
              value: item,
            }))}
            error={errors?.idType?.message}
            defaultValue={
              getIdTypeName(generalUserInfo?.idType as IdType) || '-'
            }
            withCopy={!edit}
          />
          {!edit && (
            <>
              <GeneralUserInfoRow
                title="ID Exp. Date"
                control={control}
                type="text"
                name="idExpDate"
                defaultValue={
                  formatDate(generalUserInfo?.idExpDate as string) || '-'
                }
                withCopy={!edit}
                disabled
              />
              <GeneralUserInfoRow
                title="Residence country"
                control={control}
                type="autocomplete"
                disabled={!edit}
                list={countries}
                defaultValue={
                  countries?.find(
                    (item) => item.value === baseUserInformation?.countryId,
                  )?.name || '-'
                }
                withCopy={!edit}
              />
            </>
          )}
          {edit && (
            <>
              <GeneralUserInfoRow
                title="ID Exp. Date"
                control={control}
                type="date"
                name="idExpDate"
                disabled={!edit}
                pickerPisition={PickerPosition.RIGHT}
                error={errors?.idExpDate?.message}
                defaultValue={
                  formatDate(generalUserInfo?.idExpDate as string) || '-'
                }
              />
              <GeneralUserInfoRow
                title="Residence country"
                control={control}
                type="autocomplete"
                disabled={!edit}
                list={countries}
                name="countryId"
                error={errors?.countryId?.message}
              />
            </>
          )}
          <GeneralUserInfoRow
            title="City"
            control={control}
            type="text"
            disabled={!edit}
            name="city"
            error={errors?.city?.message}
            defaultValue={baseUserInformation?.city || '-'}
            withCopy={!edit}
          />
          <GeneralUserInfoRow
            title="Address line 1"
            control={control}
            type="text"
            disabled={!edit}
            name="address1"
            error={errors?.address1?.message}
            defaultValue={baseUserInformation?.address1 || '-'}
            withCopy={!edit}
          />
          <GeneralUserInfoRow
            title="Address line 2"
            control={control}
            type="text"
            disabled={!edit}
            name="address2"
            error={errors?.address2?.message}
            defaultValue={baseUserInformation?.address2 || '-'}
            withCopy={!edit}
          />
          <GeneralUserInfoRow
            title="Postal Code"
            control={control}
            type="text"
            disabled={!edit}
            name="postalCode"
            error={errors?.postalCode?.message}
            defaultValue={baseUserInformation?.postalCode || '-'}
            withCopy={!edit}
          />
        </div>
      </div>
      {edit && globalError && <div className={cn.saveError}>{globalError}</div>}
    </div>
  );
};

export default GeneralUserInfoBlock;
